/**
 * Ajout manuel d'une discussion.
 *
 * C'est la porte d'entrée pour Facebook, LinkedIn et surtout les vieux fils
 * repérés à la main : ils rankent encore dans Google et rapportent pendant des
 * années. Le hit ajouté suit ensuite exactement le même chemin que les autres.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { Card, Field, Notice } from '../components.js';
import { useAction, useAsync } from '../hooks.js';

export function ManualAdd() {
  const navigate = useNavigate();
  const sites = useAsync(() => api.sites(), []);
  const [siteId, setSiteId] = useState('');
  const platforms = useAsync(() => (siteId ? api.platforms(siteId) : Promise.resolve([])), [siteId]);
  const [platformId, setPlatformId] = useState('');
  const sources = useAsync(
    () => (platformId ? api.sources(platformId) : Promise.resolve([])),
    [platformId],
  );
  const [sourceId, setSourceId] = useState('');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [postedAt, setPostedAt] = useState('');
  const { run, busy, error } = useAction();

  const canSubmit = useMemo(
    () => Boolean(sourceId && /^https?:\/\//i.test(url) && title.trim()),
    [sourceId, url, title],
  );

  const submit = async () => {
    const hit = await run(() =>
      api.addManualHit({
        sourceId,
        url: url.trim(),
        title: title.trim(),
        body: body.trim() || undefined,
        postedAt: postedAt ? new Date(postedAt).toISOString() : undefined,
      }),
    );
    if (hit) navigate(`/hits/${hit.id}`);
  };

  return (
    <>
      <header className="topbar">
        <h1>Ajout manuel</h1>
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}

        <Notice kind="rule">
          À utiliser pour les discussions trouvées à la main : groupes Facebook, LinkedIn, forums
          sans flux, et vieux fils encore bien classés dans Google. Le hit est scoré et rédigeable
          comme les autres ; recoller deux fois la même URL ne crée pas de doublon.
        </Notice>

        <Card title="La discussion trouvée">
          <div className="row">
            <div>
              <label>Site concerné</label>
              <select
                value={siteId}
                onChange={(event) => {
                  setSiteId(event.target.value);
                  setPlatformId('');
                  setSourceId('');
                }}
              >
                <option value="">—</option>
                {(sites.data ?? []).map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Plateforme</label>
              <select
                value={platformId}
                onChange={(event) => {
                  setPlatformId(event.target.value);
                  setSourceId('');
                }}
                disabled={!siteId}
              >
                <option value="">—</option>
                {(platforms.data ?? []).map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.label} ({platform.kind})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Source</label>
              <select
                value={sourceId}
                onChange={(event) => setSourceId(event.target.value)}
                disabled={!platformId}
              >
                <option value="">—</option>
                {(sources.data ?? []).map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Field label="URL de la discussion">
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://www.facebook.com/groups/…/posts/…"
              className="mono"
            />
          </Field>
          <Field label="Titre">
            <input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field
            label="Contenu (facultatif mais recommandé)"
            help="Collez le message : c’est ce que le modèle lit pour scorer et rédiger."
          >
            <textarea value={body} onChange={(event) => setBody(event.target.value)} />
          </Field>
          <Field label="Date de publication d’origine (facultatif)">
            <input type="date" value={postedAt} onChange={(event) => setPostedAt(event.target.value)} />
          </Field>

          <button type="button" className="primary" onClick={submit} disabled={busy || !canSubmit}>
            Ajouter au flux
          </button>
        </Card>
      </div>
    </>
  );
}
